import {
    DropdownContainer,
    EmptyMessage,
    SourceBadge,
    SuggestionAvatar,
    SuggestionAvatarPlaceholder,
    SuggestionEmail,
    SuggestionItem,
    SuggestionLabel,
    SuggestionMetaColumn,
    SuggestionTextWrapper,
    SuggestionTitle,
} from './MentionTextarea.StyledComponents';
import { MentionDropdownProps, MentionSource } from './MentionTextarea.types';

const SOURCE_LABELS: Record<MentionSource, string> = {
    assignee: 'Assignee',
    poc: 'Sales PoC',
    condition: 'Condition',
    orpUser: 'ORP User',
};

// Approximate dropdown footprint, used only to decide flip/clamp placement.
const DROPDOWN_MAX_HEIGHT = 240;
const DROPDOWN_WIDTH = 240;
const GAP = 4;

/**
 * Position the dropdown just below the caret, flipping above when it would
 * overflow the bottom of the viewport and clamping horizontally so it never
 * runs off the right edge. Pure so it can be unit-tested without layout.
 */
export function computeDropdownPosition(
    caretRect: DOMRect,
    viewportWidth: number,
    viewportHeight: number,
): { top: number; left: number } {
    const spaceBelow = viewportHeight - caretRect.bottom;
    const top =
        spaceBelow < DROPDOWN_MAX_HEIGHT && caretRect.top > spaceBelow
            ? Math.max(GAP, caretRect.top - DROPDOWN_MAX_HEIGHT - GAP)
            : caretRect.bottom + GAP;

    const left = Math.max(GAP, Math.min(caretRect.left, viewportWidth - DROPDOWN_WIDTH - GAP));

    return { top, left };
}

export const MentionDropdown = ({
    suggestions,
    caretRect,
    selectedIndex,
    onSelect,
    onHover,
}: MentionDropdownProps) => {
    const { top, left } = computeDropdownPosition(caretRect, window.innerWidth, window.innerHeight);

    return (
        <DropdownContainer top={top} left={left} data-testid="mention-dropdown" role="listbox">
            {suggestions.length === 0 ? (
                <EmptyMessage data-testid="mention-no-matches">No matches</EmptyMessage>
            ) : (
                suggestions.map((suggestion, index) => (
                    <SuggestionItem
                        key={suggestion.email}
                        role="option"
                        aria-selected={index === selectedIndex}
                        isSelected={index === selectedIndex}
                        data-testid={`mention-option-${suggestion.email}`}
                        // Use onMouseDown (not onClick): mousedown fires before the
                        // editable loses focus, so the selection/insertion happens
                        // while the caret is still in place.
                        onMouseDown={(e) => {
                            e.preventDefault();
                            onSelect(suggestion);
                        }}
                        onMouseEnter={() => onHover(index)}
                    >
                        {suggestion.avatarUrl ? (
                            <SuggestionAvatar
                                src={suggestion.avatarUrl}
                                alt={`${suggestion.email} avatar`}
                                data-testid={`mention-avatar-${suggestion.email}`}
                            />
                        ) : (
                            <SuggestionAvatarPlaceholder
                                aria-hidden
                                data-testid={`mention-avatar-placeholder-${suggestion.email}`}
                            />
                        )}
                        <SuggestionTextWrapper>
                            <SuggestionLabel>{suggestion.label}</SuggestionLabel>
                            <SuggestionEmail>{suggestion.email}</SuggestionEmail>
                        </SuggestionTextWrapper>
                        <SuggestionMetaColumn>
                            <SourceBadge source={suggestion.source}>
                                {SOURCE_LABELS[suggestion.source]}
                            </SourceBadge>
                            {suggestion.title ? (
                                <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                            ) : null}
                        </SuggestionMetaColumn>
                    </SuggestionItem>
                ))
            )}
        </DropdownContainer>
    );
};
